import {BlenderNodeName, ExchangeNodeName} from "#material/exchange/node-name"

/**
 * Exchange format node types must exist in every Blender version we support, so a few legacy node names are
 * substituted on the way out and restored on the way in. Separate/Combine RGB and HSV are absent from
 * Blender 5.x; Separate/Combine Color with a mode covers both and is identical across 4.2 and 5.2, so
 * the substitution needs no version check.
 */

export type BlenderNodeSubstitution = {
    blenderNodeName: BlenderNodeName
    /** Written on the way out, matched on the way in. Required only where substitutions share a Blender node. */
    addedParameters?: readonly {name: string; value: string}[]
    /** exchange socket name -> Blender socket name */
    socketsToBlender: {[exchangeSocketName: string]: string}
    /** Blender socket name -> exchange socket name */
    socketsFromBlender: {[blenderName: string]: string}
    /** Where a link comes back under another name than the value of the same socket */
    linksFromBlender?: {[blenderName: string]: string}
}

const colorMode = (value: string) => [{name: "internal.mode", value}]

const substitutionsByExchangeNodeName: Partial<Record<ExchangeNodeName, BlenderNodeSubstitution>> = {
    ShaderNodeSeparateRGB: {
        blenderNodeName: BlenderNodeName.ShaderNodeSeparateColor,
        addedParameters: colorMode("RGB"),
        socketsToBlender: {Image: "Color", R: "Red", G: "Green", B: "Blue"},
        socketsFromBlender: {Color: "Image", Red: "R", Green: "G", Blue: "B"},
    },
    ShaderNodeSeparateHSV: {
        blenderNodeName: BlenderNodeName.ShaderNodeSeparateColor,
        addedParameters: colorMode("HSV"),
        socketsToBlender: {Image: "Color", Color: "Color", H: "Red", S: "Green", V: "Blue"},
        socketsFromBlender: {Color: "Image", Red: "H", Green: "S", Blue: "V"},
    },
    ShaderNodeCombineRGB: {
        blenderNodeName: BlenderNodeName.ShaderNodeCombineColor,
        addedParameters: colorMode("RGB"),
        socketsToBlender: {R: "Red", G: "Green", B: "Blue", Image: "Color"},
        socketsFromBlender: {Red: "R", Green: "G", Blue: "B", Color: "Image"},
    },
    ShaderNodeCombineHSV: {
        blenderNodeName: BlenderNodeName.ShaderNodeCombineColor,
        addedParameters: colorMode("HSV"),
        socketsToBlender: {H: "Red", S: "Green", V: "Blue", Image: "Color", Color: "Color"},
        socketsFromBlender: {Red: "H", Green: "S", Blue: "V", Color: "Color"},
        // the output's value is stored under the legacy setting color, a link out of it under the output image
        linksFromBlender: {Color: "Image"},
    },
}

/**
 * Parameter values Blender spells differently from us. Blender has no plain "Linear" colour space; raw
 * data is "Non-Color". Coming the other way needs no table: every Blender value we accept is stored as
 * Blender spells it, apart from the values known-exchange-gaps records as unsendable.
 */
const parameterValuesToBlender: Partial<Record<ExchangeNodeName, {[parameterName: string]: {[value: string]: string}}>> = {
    ShaderNodeTexImage: {
        "internal.image.colorspace_settings.name": {Linear: "Non-Color", "Non-Color": "Non-Color", sRGB: "sRGB"},
    },
}

export const substituteParameterValueToBlender = <T>(exchangeNodeName: string, parameterName: string, value: T): T => {
    const mapped = parameterValuesToBlender[exchangeNodeName as ExchangeNodeName]?.[parameterName]
    return typeof value === "string" && mapped ? ((mapped[value] ?? value) as T) : value
}

/** Blender lets any node be muted, but only these offer the toggle in the material editor. */
const disableableNodes = new Set<ExchangeNodeName>([
    ExchangeNodeName.ShaderNodeBrightContrast,
    ExchangeNodeName.ShaderNodeGamma,
    ExchangeNodeName.ShaderNodeHueSaturation,
    ExchangeNodeName.ShaderNodeInvert,
    ExchangeNodeName.ShaderNodeRGBCurve,
])

export const canBeDisabled = (exchangeNodeName: string): boolean => disableableNodes.has(exchangeNodeName as ExchangeNodeName)

/**
 * Settings Blender has nowhere to put, which the addon keeps as custom properties so they survive a
 * round trip. Without this they reach Blender, land nowhere, and are gone from the next revision.
 */
const persistedParameters: Partial<Record<ExchangeNodeName, readonly string[]>> = {
    ShaderNodeOutputMaterial: ["shadow_catcher", "Name", "internal.displacement.enabled"],
    ShaderNodeTexVoronoi: ["Fac", "internal.coloring", "internal.gradient_type"],
    ShaderNodeSubsurfaceScattering: ["Sharpness", "Texture Blur"],
    ShaderNodeVectorMath: ["internal.use_clamp"],
    ShaderNodeTexCoord: ["Coordinate Type"],
    ShaderNodeTexImage: ["Colorspace", "internal.disable.progressive.loading", "internal.force.original.resolution"],
    ShaderNodeNormalMap: ["Image"],
    ShaderNodeBump: ["Height_dx", "Height_dy"],
}

export const PERSIST_PREFIX = "persist."
const INTERNAL_PREFIX = "internal."

export const isPersistedParameter = (exchangeNodeName: string, exchangeSocketName: string): boolean =>
    persistedParameters[exchangeNodeName as ExchangeNodeName]?.includes(exchangeSocketName) ?? false

/**
 * Blender colour sockets are RGBA and reject a 3-component sequence outright, while the node
 * properties behind internal. are plain RGB and reject a 4-component one just as hard.
 */
export const toBlenderParameterValue = <T>(name: string, type: unknown, value: T): T =>
    !name.startsWith(INTERNAL_PREFIX) && type === "color" && Array.isArray(value) && value.length === 3 ? ([...value, 1.0] as T) : value

export const blenderNodeSubstitutionFor = (exchangeNodeName: string): BlenderNodeSubstitution | undefined =>
    substitutionsByExchangeNodeName[exchangeNodeName as ExchangeNodeName]

export const substituteSocketToBlender = (exchangeNodeName: string, socketName: string): string =>
    substitutionsByExchangeNodeName[exchangeNodeName as ExchangeNodeName]?.socketsToBlender[socketName] ?? socketName

export const substituteSocketFromBlender = (exchangeNodeName: string, blenderSocketName: string): string =>
    substitutionsByExchangeNodeName[exchangeNodeName as ExchangeNodeName]?.socketsFromBlender[blenderSocketName] ?? blenderSocketName

export const substituteLinkFromBlender = (exchangeNodeName: string, blenderSocketName: string): string =>
    substitutionsByExchangeNodeName[exchangeNodeName as ExchangeNodeName]?.linksFromBlender?.[blenderSocketName] ??
    substituteSocketFromBlender(exchangeNodeName, blenderSocketName)

/** Returns undefined when no substitution claims the node, including Separate Color in a mode we do not model. */
export const exchangeNodeNameFor = (blenderNodeName: string, parameters: readonly {name: string; value: unknown}[]): ExchangeNodeName | undefined =>
    Object.entries(substitutionsByExchangeNodeName).find(
        ([, {blenderNodeName: name, addedParameters}]) =>
            name === blenderNodeName &&
            (addedParameters ?? []).every((added) => parameters.some((parameter) => parameter.name === added.name && parameter.value === added.value)),
    )?.[0] as ExchangeNodeName | undefined
